import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export const Cart = () => {
  const { t, i18n } = useTranslation();
  const { cart, cartTotal, updateCartItem, removeFromCart } = useApp();
  const lang = i18n.language;

  const handleUpdateQuantity = async (productId, currentQty, delta, color) => {
    const newQty = currentQty + delta;
    if (newQty < 1) return;
    await updateCartItem(productId, newQty, color);
  };

  const handleRemove = async (productId, color, productName) => {
    await removeFromCart(productId, color);
    toast.success(t('cart.remove') + ' ✓', {
      description: productName,
      style: { background: '#1A1A1A', border: '1px solid #333', color: '#fff' }
    });
  };

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen pt-20 flex flex-col items-center justify-center px-4" data-testid="cart-empty">
        <ShoppingBag className="w-20 h-20 text-[#333333] mb-6" />
        <h1 className="font-heading text-2xl sm:text-3xl text-white font-bold mb-4">
          {t('cart.empty')}
        </h1>
        <Link to="/shop">
          <Button className="btn-primary">
            {t('cart.continueShopping')}
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="cart-page">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="font-heading text-3xl sm:text-4xl text-white font-bold mb-8">
          {t('cart.title')}
        </h1>

        {/* Cart Items */}
        <div className="space-y-4 mb-8">
          {cart.items.map((item, index) => (
            <div 
              key={`${item.product_id}-${item.color}-${index}`}
              className="bg-[#1A1A1A] border border-[#333333] p-4 flex gap-4"
              data-testid={`cart-item-${item.product_id}`}
            >
              {/* Image */}
              <Link to={`/product/${item.product_id}`} className="shrink-0">
                <img
                  src={item.product?.image_url}
                  alt={item.product?.name?.[lang] || item.product?.name?.en}
                  className="w-24 h-24 object-cover"
                />
              </Link>

              {/* Info */}
              <div className="flex-1 flex flex-col justify-between">
                <div>
                  <Link to={`/product/${item.product_id}`}>
                    <h3 className="font-heading text-white font-bold text-sm uppercase tracking-wider hover:text-[#FF0099] transition-colors">
                      {item.product?.name?.[lang] || item.product?.name?.en}
                    </h3>
                  </Link>
                  {item.color && (
                    <p className="text-[#A3A3A3] text-sm mt-1">Color: {item.color}</p>
                  )}
                </div>

                <div className="flex items-center justify-between mt-2">
                  {/* Quantity Controls */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleUpdateQuantity(item.product_id, item.quantity, -1, item.color)}
                      className="w-8 h-8 bg-[#333333] flex items-center justify-center hover:bg-[#FF0099] transition-colors"
                      data-testid={`qty-decrease-${item.product_id}`}
                    >
                      <Minus className="w-4 h-4 text-white" />
                    </button>
                    <span className="text-white font-bold w-8 text-center">{item.quantity}</span>
                    <button
                      onClick={() => handleUpdateQuantity(item.product_id, item.quantity, 1, item.color)}
                      className="w-8 h-8 bg-[#333333] flex items-center justify-center hover:bg-[#FF0099] transition-colors"
                      data-testid={`qty-increase-${item.product_id}`}
                    >
                      <Plus className="w-4 h-4 text-white" />
                    </button>
                  </div>

                  {/* Price */}
                  <p className="text-[#FF0099] font-bold">
                    €{((item.product?.price || 0) * item.quantity).toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Remove Button */}
              <button
                onClick={() => handleRemove(item.product_id, item.color, item.product?.name?.[lang] || item.product?.name?.en)}
                className="text-[#A3A3A3] hover:text-[#FF0000] transition-colors self-start"
                data-testid={`remove-item-${item.product_id}`}
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="bg-[#1A1A1A] border border-[#333333] p-6">
          <div className="flex items-center justify-between mb-6">
            <span className="text-white font-bold text-lg">{t('cart.total')}</span>
            <span className="text-[#FF0099] font-heading font-bold text-3xl">
              €{cartTotal.toFixed(2)}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/shop" className="flex-1">
              <Button className="btn-secondary w-full">
                {t('cart.continueShopping')}
              </Button>
            </Link>
            <Link to="/checkout" className="flex-1" data-testid="checkout-btn">
              <Button className="btn-primary w-full">
                {t('cart.checkout')}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
